import { Stack, useRouter } from 'expo-router';
import { useState } from 'react';
import {
  ActivityIndicator,
  Alert,
  KeyboardAvoidingView,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';

import { contentReportManager } from '@/lib/managers/ContentReportManager';
import { reportContextStore } from '@/lib/report/ReportContextStore';
import { CONTENT_REPORT_LIMITS } from '@/types/content-report';
import { Colors } from '@/constants/theme';
import { Spacing } from '@/constants/spacing';
import { getBodyFont, getHeadingFont } from '@/constants/typography';
import { useColorScheme } from '@/hooks/use-color-scheme';

/**
 * Report AI Content Screen
 *
 * Shared destination for reporting AI-generated content. Reached either from an
 * AI surface (which stashes the exact content in ReportContextStore beforehand)
 * or generically from Settings (no content attached). Required by Google Play's
 * Generative AI policy.
 */
export default function ReportAIContentScreen() {
  const colorScheme = useColorScheme();
  const palette = Colors[colorScheme ?? 'light'];
  const router = useRouter();

  // peek() is non-mutating, so reading it in the state initializer is safe even
  // under StrictMode double-render. Cleared on successful submit.
  const [context] = useState(() => reportContextStore.peek());
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const result = context
        ? await contentReportManager.submit(context, reason)
        : await contentReportManager.submitGeneric(reason);

      if (result.success) {
        reportContextStore.clear();
        Alert.alert('Report submitted', 'Thanks — our team will review this.', [
          { text: 'OK', onPress: () => router.back() },
        ]);
      } else {
        Alert.alert('Could not report', result.error);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const canSubmit = context !== null || reason.trim().length > 0;

  return (
    <>
      <Stack.Screen options={{ title: 'Report AI Content', headerShown: true }} />
      <KeyboardAvoidingView
        style={[styles.container, { backgroundColor: palette.background }]}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
        <ScrollView contentContainerStyle={styles.content} keyboardShouldPersistTaps="handled">
          <Text style={[styles.intro, { color: palette.text, fontFamily: getBodyFont() }]}>
            {context
              ? 'You’re reporting the AI-generated content shown below. Tell us what was wrong (optional), then submit. Our team will review it.'
              : 'Use this form to report AI-generated content that was inaccurate, inappropriate, or offensive. Describe what you saw and submit — our team will review it.'}
          </Text>

          {context ? (
            <View style={styles.block}>
              <Text style={[styles.label, { color: palette.textMuted, fontFamily: getBodyFont() }]}>
                Content being reported
              </Text>
              <View style={[styles.snapshotBox, { borderColor: palette.border, backgroundColor: palette.background }]}>
                <ScrollView style={styles.snapshotScroll} nestedScrollEnabled>
                  <Text style={[styles.snapshotText, { color: palette.text, fontFamily: getBodyFont() }]}>
                    {context.snapshot}
                  </Text>
                </ScrollView>
              </View>
            </View>
          ) : null}

          <View style={styles.block}>
            <Text style={[styles.label, { color: palette.textMuted, fontFamily: getBodyFont() }]}>
              {context ? 'What was wrong? (optional)' : 'What was wrong?'}
            </Text>
            <TextInput
              style={[
                styles.reasonInput,
                { borderColor: palette.border, color: palette.text, fontFamily: getBodyFont() },
              ]}
              placeholder="Describe the issue…"
              placeholderTextColor={palette.textMuted}
              value={reason}
              onChangeText={setReason}
              multiline
              maxLength={CONTENT_REPORT_LIMITS.REASON_MAX_CHARS}
              textAlignVertical="top"
              editable={!isSubmitting}
            />
          </View>

          <Pressable
            onPress={handleSubmit}
            disabled={!canSubmit || isSubmitting}
            style={[
              styles.submitButton,
              { backgroundColor: palette.tint, opacity: !canSubmit || isSubmitting ? 0.5 : 1 },
            ]}>
            {isSubmitting ? (
              <ActivityIndicator color="#fff" />
            ) : (
              <Text style={[styles.submitText, { fontFamily: getHeadingFont() }]}>Submit report</Text>
            )}
          </Pressable>
        </ScrollView>
      </KeyboardAvoidingView>
    </>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    padding: Spacing.xl,
    gap: Spacing.lg,
  },
  intro: {
    fontSize: 14,
    lineHeight: 21,
  },
  block: {
    gap: Spacing.xs,
  },
  label: {
    fontSize: 12,
    fontWeight: '600',
  },
  snapshotBox: {
    borderWidth: 1,
    borderRadius: 10,
    padding: Spacing.md,
  },
  snapshotScroll: {
    maxHeight: 160,
  },
  snapshotText: {
    fontSize: 13,
    lineHeight: 19,
  },
  reasonInput: {
    borderWidth: 1,
    borderRadius: 10,
    padding: Spacing.md,
    minHeight: 120,
    fontSize: 14,
  },
  submitButton: {
    borderRadius: 12,
    paddingVertical: Spacing.md,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: Spacing.sm,
  },
  submitText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
});
